import React, { useEffect } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Heading3, Italic, Link2, List, ListOrdered, Redo2, Undo2 } from 'lucide-react';
import { richTextHtml } from '../lib/richText';

interface Props {
  value: string;
  onChange: (html: string) => void;
  /** Etichetta letta dagli screen reader per l'area di testo. */
  label: string;
}

const ToolbarButton: React.FC<{ label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({
  label,
  active,
  disabled,
  onClick,
  children,
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    aria-pressed={active}
    disabled={disabled}
    // Senza preventDefault il clic toglie il fuoco all'editor e perde la selezione.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`flex h-10 w-10 md:h-8 md:w-8 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${
      active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

/**
 * Editor di testo formattato per la dashboard: grassetto, corsivo,
 * sottotitoli, elenchi e link, senza scrivere HTML. Salva HTML che le pagine
 * pubbliche mostrano con richTextHtml (sanificato).
 */
const RichTextField: React.FC<Props> = ({ value, onChange, label }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
      }),
    ],
    content: richTextHtml(value),
    editorProps: {
      attributes: {
        'aria-label': label,
        class: 'rich-text min-h-[140px] px-4 py-3 text-sm text-foreground focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? '' : editor.getHTML()),
  });

  // Testo cambiato da fuori (Annulla modifiche, Ripristina): l'editor si allinea.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (current !== value) editor.commands.setContent(richTextHtml(value), { emitUpdate: false });
  }, [editor, value]);

  const state = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor && {
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        heading: editor.isActive('heading', { level: 3 }),
        bullet: editor.isActive('bulletList'),
        ordered: editor.isActive('orderedList'),
        link: editor.isActive('link'),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo(),
      },
  });

  if (!editor || !state) return null;

  const setLink = () => {
    if (state.link) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Indirizzo del link (es. https://… oppure /products)');
    if (!url?.trim()) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card focus-within:ring-2 focus-within:ring-ring/40">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/50 px-2 py-1">
        <ToolbarButton label="Grassetto" active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Corsivo" active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Sottotitolo"
          active={state.heading}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton label="Elenco puntato" active={state.bullet} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Elenco numerato" active={state.ordered} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label={state.link ? 'Togli il link' : 'Aggiungi un link'} active={state.link} onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarButton label="Annulla" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Ripeti" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextField;
